import { N_, formatNumber, t } from '../i18n/i18n';

/**
 * Text the server sends, in the player's language (2026-10-16).
 *
 * The server speaks English. Most of what it sends is a fixed sentence out of
 * a backend table, and those are marked in the generated `served-copy.ts`, so
 * `t()` on them simply works. Three kinds are not, and this file handles them:
 *
 * - **Error codes.** Many game services refuse with a bare code
 *   (`name_taken`, `charge_cap_exceeded`) and the hub used to print it as-is.
 *   A code is not a sentence a translator can work with, so each one gets an
 *   English sentence here, and that sentence is the key.
 * - **Lines built from numbers** — a floor's threat list
 *   (`contracts/config/stratum-info.ts`). Matched by shape and re-said
 *   through a key with placeholders, so word order can change.
 * - **Anything else** reads exactly as the server sent it.
 *
 * `served-copy-sync.ts` lists any backend code that has no sentence here.
 */

/** Backend error code -> what the player is told. Keyed by the code the server sends. */
const CODE_MESSAGE: Record<string, string> = {
  // Characters and talents
  character_not_owned: N_('That delver is not yours.'),
  character_not_found: N_('That delver no longer exists.'),
  invalid_class: N_('That class does not exist.'),
  no_free_slots: N_('Every delver slot is taken.'),
  name_taken: N_('That name is already taken'),
  character_in_run: N_('That delver is in a run right now.'),
  unknown_node: N_('That talent does not exist.'),
  no_unspent_points: N_('No talent points left to spend.'),
  tier_locked: N_('That tier is still locked.'),
  node_maxed: N_('That talent is already at its highest rank.'),
  capstone_already_taken: N_('You already chose a capstone. Respec to change it.'),
  // Commissions
  commission_not_found: N_('That commission no longer exists.'),
  commission_not_on_board: N_('That commission is not on today’s board.'),
  already_delivered: N_('Already delivered.'),
  not_enough_items: N_('You are not holding enough of those.'),
  // Shop, satchel, Rites, contracts
  invalid_quantity: N_('That is not a quantity you can buy.'),
  invalid_count: N_('That is not a quantity you can buy.'),
  daily_consumable_cap_exceeded: N_('You have bought as many of those as you can today.'),
  charge_cap_exceeded: N_('Your satchel cannot hold any more of those.'),
  already_at_max_slots: N_('Already at the most slots there are.'),
  contract_not_found: N_('That contract no longer exists.'),
  contract_exhausted: N_('That contract is spent.'),
  rite_not_found: N_('That Rite no longer exists.'),
  rite_not_owned: N_('You do not own that Rite.'),
  rite_stock_full: N_('You cannot hold any more of that Rite.'),
  daily_exchange_cap_exceeded: N_('You have exchanged as much as you can today.'),
  amount_must_be_positive: N_('Enter an amount above zero.'),
  // Cosmetics, arms, gear, stash
  already_owned: N_('You already own that.'),
  not_owned: N_('You do not own that.'),
  emotes_are_not_equipped: N_('Emotes are not equipped — every delver can play the ones you own.'),
  wrong_class_for_skin: N_('That skin is for a different class.'),
  equipment_not_found: N_('That piece no longer exists.'),
  equipment_is_free: N_('That piece is free — it comes with the account.'),
  equipment_not_owned: N_('You do not own that piece.'),
  invalid_equipment: N_('That piece cannot go in that slot.'),
  invalid_gear: N_('That piece cannot go in that slot.'),
  gear_not_owned: N_('You do not own that piece.'),
  gear_not_found: N_('That piece no longer exists.'),
  stash_item_not_found: N_('That item is no longer in your stash.'),
  not_a_gear_item: N_('That item is not something you can wear.'),
  // Leaderboard
  unknown_mode: N_('That mode does not exist.'),
  unknown_sort: N_('That ranking does not exist.'),
  // Parties (matchmaking.gateway sends the party code, lower-case, as the message)
  cannot_invite_self: N_('You cannot invite yourself.'),
  target_already_partied: N_('That player is already in a party.'),
  target_already_has_pending_invite: N_('That player already has an invite waiting.'),
  not_leader: N_('Only the party leader can do that.'),
  mode_mismatch: N_('That party is queued for a different mode.'),
  party_full: N_('That party is full.'),
  no_pending_invite: N_('That invite has expired.'),
};

/** Fixed threat lines `stratum-info.ts` can send. */
export const STATIC_THREATS: readonly string[] = [N_('Baseline threat'), N_('Rares only in the vault room')];

/** Lines the server builds from numbers: shape, key, and the names of the captured parts. */
const PATTERNS: readonly (readonly [RegExp, string, readonly string[]])[] = [
  [/^(\d+)-(\d+) rooms to the Warden$/, N_('{min}-{max} rooms to the Warden'), ['min', 'max']],
  [/^Bonewrights ×([\d.]+) as common$/, N_('Bonewrights ×{n} as common'), ['n']],
  [/^Bonewrights hunt in groups of (\d+)$/, N_('Bonewrights hunt in groups of {n}'), ['n']],
  [/^Rattler swarms \+(\d+)$/, N_('Rattler swarms +{n}'), ['n']],
  [/^The dead carry ×([\d.]+) health$/, N_('The dead carry ×{n} health'), ['n']],
  [/^(\d+%) rare chest chance$/, N_('{pct} rare chest chance'), ['pct']],
  [/^You must be at least (\d+)$/, N_('You must be at least {n}'), ['n']],
];

/**
 * A server string, translated if this build knows it. Never throws and never
 * blanks: unknown text comes back unchanged.
 */
export function serverText(text: string | null | undefined): string {
  if (!text) return text ?? '';
  const code = text.toLowerCase();
  const sentence = Object.hasOwn(CODE_MESSAGE, code) ? CODE_MESSAGE[code] : undefined;
  if (sentence) return t(sentence);
  for (const [shape, key, names] of PATTERNS) {
    const m = shape.exec(text);
    // Decimals the server wrote in English ("×1.4") read in the player's own.
    if (m) return t(key, Object.fromEntries(names.map((name, i) => [name, /^\d+\.\d+$/.test(m[i + 1]) ? formatNumber(Number(m[i + 1])) : m[i + 1]])));
  }
  return t(text);
}
